import { useState, useEffect } from 'react';

/**
 * Custom hook to extract a thumbnail image from a video blob URL
 * Works on all browsers including Safari iOS
 */
export const useVideoThumbnail = (videoBlobUrl: string | null): string | null => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!videoBlobUrl) {
      setThumbnailUrl(null);
      return;
    }

    const video = document.createElement('video');
    video.src = videoBlobUrl;
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.muted = true;
    video.preload = 'metadata';

    const handleLoadedData = () => {
      video.currentTime = 0.1; // Seek to 0.1 second to get first frame
    };

    const handleSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        if (ctx && canvas.width > 0 && canvas.height > 0) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setThumbnailUrl(dataUrl);
        }
      } catch (error) {
        console.error('Failed to extract video thumbnail:', error);
      } finally {
        video.remove();
      }
    };

    const handleError = () => {
      console.error('Video thumbnail extraction failed');
      video.remove();
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('error', handleError);
    video.load();

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
      video.remove();
    };
  }, [videoBlobUrl]);

  return thumbnailUrl;
};

